import { useEffect, useState, useRef } from 'react';
import useAuthStore from '../stores/useAuthStore';
import useUserStore from '../stores/useUserStore';
import useAICreditStore from '../stores/useAICreditStore';
import useCircuitRunStore from '../stores/useCircuitRunStore';
import useConfigStore from '../stores/useConfigStore';
import { initCloudStorage } from '../utils/storage';
import { identifyUser } from '../utils/telemetry';
import Login from '../pages/Login';

/** Max time to wait for Firebase auth to resolve before showing login */
const AUTH_TIMEOUT_MS = 10000;

/**
 * AuthGate wraps the app and handles:
 * 1. Auth state initialization
 * 2. Showing login if not authenticated
 * 3. Initializing cloud storage before rendering children
 * 4. Graceful timeout if auth/network is slow (desktop startup)
 */
export default function AuthGate({ children }) {
    const user = useAuthStore(s => s.user);
    const loading = useAuthStore(s => s.loading);
    const initAuth = useAuthStore(s => s.initAuth);
    const initFromAuth = useUserStore(s => s.initFromAuth);
    const [storageReady, setStorageReady] = useState(false);
    const [authTimedOut, setAuthTimedOut] = useState(false);
    const timeoutRef = useRef(null);

    // Initialize Firebase auth listener on mount
    useEffect(() => {
        initAuth();

        // Safety net: if auth doesn't resolve in 10s (e.g. no network on
        // desktop startup), stop showing spinner and let onAuthStateChanged
        // resolve naturally from IndexedDB cache when it can.
        timeoutRef.current = setTimeout(() => {
            const state = useAuthStore.getState();
            if (state.loading) {
                console.warn('[AuthGate] Auth timed out — forcing loading=false');
                // Force-resolve loading so we don't hang forever.
                // If Firebase has a cached user in IndexedDB, onAuthStateChanged
                // will still fire and set user — this just unblocks the spinner.
                useAuthStore.setState({ loading: false });
                setAuthTimedOut(true);
            }
        }, AUTH_TIMEOUT_MS);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    // When user logs in, initialize cloud storage
    useEffect(() => {
        if (user) {
            setAuthTimedOut(false);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            initCloudStorage(user.uid)
                .then(() => {
                    initFromAuth(user);

                    // Identify user in PostHog
                    identifyUser(user.uid, {
                        email: user.email,
                        name: user.displayName,
                    });

                    useUserStore.getState().initSubscriptionListener(user.uid);
                    useConfigStore.getState().init(user.uid);
                    useAICreditStore.getState().initCredits(user.uid);
                    useCircuitRunStore.getState().init(user.uid);
                    setStorageReady(true);
                })
                .catch(err => {
                    // Cloud storage init failed — still let the app boot with local data
                    console.warn('[AuthGate] Cloud storage init failed — booting with local cache:', err.message);
                    initFromAuth(user);
                    setStorageReady(true);
                });
        } else {
            useUserStore.getState().cancelSubscriptionListener?.();
            useConfigStore.getState().cleanup();
            useAICreditStore.getState().cleanup();
            useCircuitRunStore.getState().cleanup();
            setStorageReady(false);
        }
    }, [user]);

    // Loading auth state
    if (loading) {
        return (
            <div className="login-loading">
                <div className="login-loading__spinner" />
            </div>
        );
    }

    // Not authenticated
    if (!user) {
        return <Login />;
    }

    // Waiting for storage sync
    if (!storageReady) {
        return (
            <div className="login-loading">
                <div className="login-loading__spinner" />
                {authTimedOut && (
                    <p style={{ marginTop: 'var(--space-md)', opacity: 0.6, fontSize: 'var(--font-xxs)' }}>
                        Reconnecting…
                    </p>
                )}
            </div>
        );
    }

    // Authenticated and storage ready
    return children;
}
